import { grassTypeToGuideSlug } from '@/lib/grass-slug'

export interface ReferenceImage {
  /** Path under /public (served statically). */
  src: string
  /**
   * Short label shown under this image in the gallery, e.g. "Established lawn",
   * "Boat-shaped blade tip · the ID feature". Optional — single-image species
   * omit it (the figure caption already names the species).
   */
  caption?: string
  /** Photographer/author credit. null = no attribution required (CC0 / public domain). */
  credit: string | null
  /** Human-readable license, e.g. "CC BY-SA 4.0", "CC0", "Public domain". */
  license: string
  /**
   * Canonical attribution + license source page: a Wikimedia Commons `File:` page
   * or an iNaturalist observation page (`inaturalist.org/observations/<id>`). The
   * result/compare pages derive the "via <source>" label from this host.
   */
  sourceUrl: string
}

export interface ReferencePhoto extends ReferenceImage {
  /**
   * Extra reference images (blade close-up, seed head, growth habit) shown as
   * swipeable/tappable alternates after the lead. The lead is this record's own
   * src/caption/credit; `alternates` are additional facets that reinforce the ID
   * (recognition over recall). Omit for species with a single verified photo.
   */
  alternates?: ReferenceImage[]
}

/**
 * Curated, species-accurate reference photos keyed by guide slug. Shown on the
 * grass-ID result page as a side-by-side "your photo vs reference" so users can
 * visually confirm the match (recognition over recall, per
 * obsidian/research/growth-design/amber-alert.md). Only EXACT species matches are
 * used -- a region-default fallback would defeat the purpose. A species may carry
 * a small gallery (lead + `alternates`: blade, seed head, growth habit) that the
 * result page renders with a thumbnail strip + tap-to-zoom lightbox.
 *
 * Sources: botanist-curated Wikimedia Commons files (morphology-verified) and, where
 * Commons had no maintained-turf lead, iNaturalist RESEARCH-GRADE observations (2+
 * agreeing community IDs) under commercial-OK licenses (CC0 / CC BY 4.0). Fetched/
 * resized by scripts/fetch-grass-reference-photos.ts. License + attribution travel
 * with each record. CC-BY/BY-SA photos render a credit line on the result page via
 * getGrassReferencePhotoMeta / getGrassReferenceImages; only attribution-free (CC0 /
 * public-domain) photos are exposed to decorative surfaces with no room for a
 * credit, via getGrassReferencePhoto (see below).
 */
// TODO(reference-photo, 2026-07-11): Annual Ryegrass and Mixed Cool-Season Blend
// remain photo-less on purpose, re-confirmed by a second bounded search
// 2026-07-11 (Commons, USDA PLANTS, Flickr CC) and a THIRD on 2026-09-15: all
// 238 commercial-OK per-photo-licensed photos on research-grade Lolium
// multiflorum observations are seed-head macros, in-hand specimens, or
// roadside/waste-ground plants. Not one maintained overseeded turf. Stop
// re-searching iNaturalist for this one; it needs a different source. Blend was
// not re-searched, by instruction. Annual rye: every free-licensed
// L. multiflorum image is a botanical close-up, herbarium sheet, or cover-crop
// field — no maintained overseeded turf (the prior rejected candidate was
// https://commons.wikimedia.org/wiki/File:Italiaans_raaigras_(Lolium_multiflorum).jpg,
// Rasbak, CC BY-SA 3.0, wild clumps). Blend: the only viable maintained-lawn
// candidate (commons File:Kentucky Blue Grass.jpg) is author-labeled as pure
// KBG, so using it for the blend would misrepresent the species — the exact
// failure this omission exists to avoid. Revisit only with a verified
// overseeded-turf shot (annual rye) or a visibly multi-texture lawn photo
// (blend). Kikuyugrass was visually verified + added 2026-07-10.
const REFERENCE_PHOTOS: Record<string, ReferencePhoto> = {
  // Gallery re-sourced 2026-07-11 (first multi-image species). The single lead
  // was a botanist blade macro on black (Douglas Goldman) — botanically perfect
  // but it read as a lab specimen next to the user's lush lawn, breaking the
  // "does my lawn look like that?" verification this slot exists for. The lead is
  // now a maintained-lawn turf shot; the blade macro + a seed-head panicle stay
  // on as captioned alternates. All CC BY-SA 4.0. See scripts/fetch-grass-reference-photos.ts.
  'kentucky-bluegrass': {
    src: '/examples/kentucky-bluegrass-example.jpg',
    caption: 'Established lawn',
    credit: 'Pilot138-17',
    license: 'CC BY-SA 4.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Kentucky_Blue_Grass.jpg',
    alternates: [
      {
        src: '/examples/kentucky-bluegrass-blade.jpg',
        caption: 'Boat-shaped blade tip · the ID feature',
        credit: 'Douglas Goldman',
        license: 'CC BY-SA 4.0',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:PoaPratensis1.jpg',
      },
      {
        src: '/examples/kentucky-bluegrass-seedhead.jpg',
        caption: 'Seed head · open panicle',
        credit: 'Ryan Hodnett',
        license: 'CC BY-SA 4.0',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:Kentucky_Bluegrass_(Poa_pratensis)_-_Guelph,_Ontario.jpg',
      },
    ],
  },
  // Tall fescue gallery, re-sourced 2026-07-11 (KBG rollout). The prior lead was a
  // single coarse blade on gravel (Harry Rose) — accurate coarse-blade ID but a
  // specimen, not a lawn. Lead is now dense coarse foliage; alternates add growth
  // habit + the branched-panicle seed head. Lead/seedhead from iNaturalist
  // research-grade observations (2+ agreeing IDs).
  // Primary swapped 2026-09-15 to the stand-back clump-in-a-sward frame. The
  // /compare/* panels render ONLY each species' primary, so the primaries have
  // to sit at a comparable camera distance; the coarse-blade close-up that used
  // to lead dwarfed the bermuda and zoysia panels next to it. It is now the
  // first alternate. This is the widest verified tall-fescue frame in the
  // CC0/CC BY research-grade pool — no mown tall-fescue lawn canopy exists
  // there, because the species is photographed for its seed head.
  'tall-fescue': {
    src: '/examples/tall-fescue-habit.jpg',
    caption: 'Upright clump in a grassy sward',
    credit: null,
    license: 'CC0',
    sourceUrl: 'https://www.inaturalist.org/observations/46413391',
    alternates: [
      {
        src: '/examples/tall-fescue-example.jpg',
        caption: 'Coarse, ribbed blades',
        credit: 'wild_wind',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/63524956',
      },
      {
        src: '/examples/tall-fescue-blade.jpg',
        caption: 'Prominent ribbed veins on a coarse blade',
        credit: 'Alex Zorach',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/50781104',
      },
      {
        src: '/examples/tall-fescue-seedhead.jpg',
        caption: 'Seed head · branched panicle',
        credit: 'Sam Kieschnick',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/117193351',
      },
    ],
  },
  // Fine fescue gallery, re-sourced 2026-07-11 (KBG rollout). Lead now leads with
  // the dense blue-green needle-fine texture (the FF recognition cue) instead of a
  // hand on dryland fescue; seed-head alternate from iNaturalist research-grade.
  'fine-fescue': {
    src: '/examples/fine-fescue-example.jpg',
    caption: 'Fine, needle-like blades',
    credit: null,
    license: 'CC0',
    sourceUrl: 'https://www.inaturalist.org/observations/273295027',
    alternates: [
      {
        src: '/examples/fine-fescue-blade.jpg',
        caption: 'Needle-fine folded blades, shown against a palm for scale',
        credit: 'Dmitriy Bochkov',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/49345682',
      },
      {
        src: '/examples/fine-fescue-seedhead.jpg',
        caption: 'Seed head · narrow panicle',
        credit: 'Sarah Johnson',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/82226406',
      },
    ],
  },
  // Perennial rye gallery, re-sourced 2026-07-11 (KBG rollout). Lead is a clean
  // lush-tuft growth-habit shot (Harry Rose, Commons); the flattened bilateral
  // spike (the Lolium seed-head ID) is the alternate, from iNaturalist research-grade.
  'perennial-ryegrass': {
    src: '/examples/perennial-ryegrass-example.jpg',
    caption: 'Lush green tufts',
    credit: 'Harry Rose',
    license: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Lolium_perenne_plant2_(7185124265).jpg',
    alternates: [
      {
        src: '/examples/perennial-ryegrass-blade.jpg',
        caption: 'Red-purple leaf sheath at the base of the shoot',
        credit: 'Millie Basden',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/113149796',
      },
      {
        src: '/examples/perennial-ryegrass-seedhead.jpg',
        caption: 'Seed head · flattened spike',
        credit: 'Quinten Wiegersma',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/27817799',
      },
    ],
  },
  // Warm-season + kikuyu galleries built 2026-09-15 to the Kentucky Bluegrass
  // standard: an established-turf recognition anchor, a blade/collar/stolon
  // macro whose caption names the feature it shows, and a seed head (or a
  // stolon where a mown lawn rarely produces one). Every added frame is an
  // iNaturalist research-grade observation under a commercial-OK PER-PHOTO
  // license (CC0 or CC BY 4.0 only), morphology-verified at full resolution.
  // The pre-existing Commons leads keep their source and license; they were
  // re-fetched square because the gallery crops to `aspect-square`.
  'bermudagrass': {
    // Re-sourced 2026-06-24: the prior Dhraxze photo was a tilted handheld snapshot
    // (ground plane ~25deg off-level) that read as "wrong orientation" full-bleed.
    // Replaced with a properly-leveled top-down turf shot.
    src: '/examples/bermudagrass-example.jpg',
    caption: 'Established lawn',
    credit: 'V C Balakrishnan',
    license: 'CC BY-SA 4.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cynodon_dactylon_(1).JPG_-_Photo_by_V_C_Balakrishnan.jpg',
    alternates: [
      {
        src: '/examples/bermudagrass-stolon.jpg',
        caption: 'Stolon · runner rooting at every node',
        credit: null,
        license: 'CC0',
        sourceUrl: 'https://www.inaturalist.org/observations/155151368',
      },
      {
        src: '/examples/bermudagrass-seedhead.jpg',
        caption: 'Seed head · finger-like spikes from one point',
        credit: null,
        license: 'CC0',
        sourceUrl: 'https://www.inaturalist.org/observations/254429552',
      },
    ],
  },
  'zoysiagrass': {
    src: '/examples/zoysiagrass-example.jpg',
    caption: 'Established lawn',
    credit: 'Krzysztof Ziarnek, Kenraiz',
    license: 'CC BY-SA 4.0',
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Zoysia_japonica_'Compadre'_kz1.jpg",
    alternates: [
      {
        src: '/examples/zoysiagrass-blade.jpg',
        caption: 'Stiff, sharply pointed narrow blades',
        credit: 'Gabriel',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/317148626',
      },
      {
        src: '/examples/zoysiagrass-seedhead.jpg',
        caption: 'Seed head · short spike raceme',
        credit: null,
        license: 'CC0',
        sourceUrl: 'https://www.inaturalist.org/observations/368459775',
      },
    ],
  },
  // Primary swapped 2026-09-15 to a canopy-scale lawn frame so the compare
  // panel matches bermuda and zoysia; the public-domain blade close-up that
  // used to lead is now the first alternate. The new lead is CC0, so the
  // species keeps its credit-less decorative surface.
  'st-augustinegrass': {
    src: '/examples/st-augustinegrass-lawn.jpg',
    caption: 'Established lawn',
    credit: null,
    license: 'CC0',
    sourceUrl: 'https://www.inaturalist.org/observations/277444565',
    alternates: [
      {
        src: '/examples/st-augustinegrass-example.jpg',
        caption: 'Wide, flat blades',
        credit: null,
        license: 'Public domain',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:Staugustinegrass.JPG',
      },
      {
        src: '/examples/st-augustinegrass-blade.jpg',
        caption: 'Blunt, boat-shaped blade tip on a constricted collar',
        credit: 'Thomas Koffel',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/31640618',
      },
      {
        src: '/examples/st-augustinegrass-stolon.jpg',
        caption: 'Stolon · one compressed sheath per node',
        credit: 'Kevin Faccenda',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/129603586',
      },
      {
        src: '/examples/st-augustinegrass-seedhead.jpg',
        caption: 'Seed head · spikelets sunk into a flattened stalk',
        credit: 'Thomas Irvine',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/248355461',
      },
    ],
  },
  // Lead swapped 2026-09-15 to a mown centipede lawn. The Vengolis photo is a
  // correct Eremochloa ophiuroides shot but shows sparse runners over bare
  // mulch, which reads as a specimen rather than the "does my lawn look like
  // that?" check this slot exists for; it stays on as a captioned alternate.
  // The new CC0 lead also makes centipede visible on the credit-less
  // decorative surfaces getGrassReferencePhoto feeds (the 60x60 city grass
  // card), which the CC-BY-SA lead could never be.
  'centipede': {
    src: '/examples/centipede-lawn.jpg',
    caption: 'Established lawn',
    credit: null,
    license: 'CC0',
    sourceUrl: 'https://www.inaturalist.org/observations/316745594',
    alternates: [
      {
        src: '/examples/centipede-stolon.jpg',
        caption: 'Stolon · short blades alternating along a red runner',
        credit: 'Raven Dandridge',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/235328104',
      },
      {
        src: '/examples/centipede-seedhead.jpg',
        caption: 'Seed head · a single spike raceme',
        credit: null,
        license: 'CC0',
        sourceUrl: 'https://www.inaturalist.org/observations/316745594',
      },
      {
        src: '/examples/centipede-example.jpg',
        caption: 'Runners spreading over bare soil',
        credit: 'Vengolis',
        license: 'CC BY-SA 4.0',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:Centipedegrass_00722.JPG',
      },
    ],
  },
  // Lead swapped 2026-09-15 to a bahia lawn. The Harry Rose photo is the
  // textbook two-branch V seed head against sky, which identifies bahia but
  // shows none of the turf; it now leads the seed-head slot instead.
  'bahiagrass': {
    src: '/examples/bahiagrass-lawn.jpg',
    caption: 'Established lawn under its own seed heads',
    credit: '岸本年郎',
    license: 'CC BY 4.0',
    sourceUrl: 'https://www.inaturalist.org/observations/135602691',
    alternates: [
      {
        src: '/examples/bahiagrass-blade.jpg',
        caption: 'Broad, flat blades over open soil',
        credit: 'Kevin Faccenda',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/108781981',
      },
      {
        src: '/examples/bahiagrass-example.jpg',
        caption: 'Seed head · two branches in a V',
        credit: 'Harry Rose',
        license: 'CC BY 2.0',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:Paspalum_notatum_flowerhead3_(7391328586).jpg',
      },
      {
        src: '/examples/bahiagrass-seed.jpg',
        caption: 'Seed · round and shiny, paired in two rows',
        credit: 'Zihao Wang',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/100260506',
      },
    ],
  },
  // Kikuyu lawn in flower (whitish stigma filaments over a light-green dense
  // mat) — visually species-verified 2026-07-10. CC BY requires the credit
  // line, which the result page renders via getGrassReferencePhotoMeta.
  'kikuyugrass': {
    src: '/examples/kikuyugrass-example.jpg',
    caption: 'Established lawn in flower',
    credit: 'Patomena',
    license: 'CC BY 3.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Pasto_kikuyo_en_flor_2.jpg',
    alternates: [
      {
        src: '/examples/kikuyugrass-blade.jpg',
        caption: 'Thick stem wrapped in overlapping pale sheaths',
        credit: 'Michele Jones',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/356507955',
      },
      {
        src: '/examples/kikuyugrass-stolon.jpg',
        caption: 'Stolon · thick, fleshy runners over bare soil',
        credit: 'Riley Pollom',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/4727154',
      },
    ],
  },
  // Primary swapped 2026-09-15 to a canopy-scale turf frame that is ALSO CC0,
  // which resolves what looked like a conflict: the compare panel needs a
  // primary at the same camera distance as bermuda and zoysia, and
  // getGrassReferencePhoto() only exposes an attribution-free LEAD to surfaces
  // with no room for a credit line (the 60x60 city grass card). A CC-BY lawn
  // shot would have bought the first at the cost of the second; this one buys
  // both. The dry Commons stand and the mown CC-BY lawn stay as alternates.
  'buffalograss': {
    src: '/examples/buffalograss-turf.jpg',
    caption: 'Established lawn',
    credit: null,
    license: 'CC0',
    sourceUrl: 'https://www.inaturalist.org/observations/358352538',
    alternates: [
      {
        src: '/examples/buffalograss-example.jpg',
        caption: 'Unirrigated stand going dormant',
        credit: null,
        license: 'CC0',
        sourceUrl: 'https://commons.wikimedia.org/wiki/File:Bouteloua_dactyloides_-_Patrick_Alexander_01.jpg',
      },
      {
        src: '/examples/buffalograss-lawn.jpg',
        caption: 'Mown buffalograss lawn',
        credit: 'Sam Kieschnick',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/85966216',
      },
      {
        src: '/examples/buffalograss-blade.jpg',
        caption: 'Fine, gray-green curling blades',
        credit: 'Sam Kieschnick',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/246228675',
      },
      {
        src: '/examples/buffalograss-seedhead.jpg',
        caption: 'Male seed head · one-sided comb on a short flag',
        credit: 'Sam Kieschnick',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/4495516',
      },
      {
        src: '/examples/buffalograss-female.jpg',
        caption: 'Female seed head · a separate bur down in the canopy',
        credit: 'Ryan Donnelly',
        license: 'CC BY 4.0',
        sourceUrl: 'https://www.inaturalist.org/observations/56074402',
      },
    ],
  },
}

/**
 * Full reference-photo record for a grass type (src + license + attribution), or
 * null if we have no exact-species reference. Use this on surfaces that can SHOW a
 * credit -- the grass-ID result page renders the credit line for CC-BY/BY-SA photos.
 */
export function getGrassReferencePhotoMeta(
  grassType: string | null | undefined,
): ReferencePhoto | null {
  const slug = grassTypeToGuideSlug(grassType)
  if (!slug) return null
  return REFERENCE_PHOTOS[slug] ?? null
}

/**
 * Ordered reference-image list for a grass type: the lead image first, then any
 * curated alternates (blade close-up, seed head, growth habit). Returns [] when
 * we have no exact-species reference. The result-page gallery renders this;
 * single-image species yield a one-element list (no gallery chrome).
 */
export function getGrassReferenceImages(
  grassType: string | null | undefined,
): ReferenceImage[] {
  const photo = getGrassReferencePhotoMeta(grassType)
  if (!photo) return []
  const { alternates, ...lead } = photo
  return [lead, ...(alternates ?? [])]
}

/**
 * Reference-photo src for a grass type, but ONLY for attribution-free photos
 * (CC0 / public domain). Returns null for CC-BY/BY-SA photos so decorative surfaces
 * with no room for a credit line (e.g. the 60x60 city grass-card icon) safely fall
 * back to the blade SVG instead of displaying an uncredited photo. Use
 * getGrassReferencePhotoMeta wherever a credit can be rendered.
 */
export function getGrassReferencePhoto(
  grassType: string | null | undefined,
): string | null {
  const photo = getGrassReferencePhotoMeta(grassType)
  if (!photo) return null
  return photo.credit ? null : photo.src
}

/**
 * Every curated reference photo keyed by guide slug, flattened to
 * (guide slug, ordered image list) pairs. SEO surfaces iterate this: the image
 * sitemap emits a /guides/<slug> entry per pair and the guide page builds
 * ImageObject JSON-LD from the same records, so license/credit metadata never
 * drifts from what the gallery renders.
 */
export function getAllGrassReferencePhotoEntries(): Array<{
  slug: string
  images: ReferenceImage[]
}> {
  return Object.entries(REFERENCE_PHOTOS).map(([slug, photo]) => {
    const { alternates, ...lead } = photo
    return { slug, images: [lead, ...(alternates ?? [])] }
  })
}
